import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Server,
  Plus,
  Edit2,
  Trash2,
  Download,
  X,
  Paperclip,
  TrendingUp,
  TrendingDown,
  Database,
  HardDrive,
  Cloud,
  GitBranch,
  Activity as ActivityIcon,
  ShieldCheck,
  Zap,
  FileText,
  Loader2,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import api from '../../api';
import toast from 'react-hot-toast';

interface InfraEntry {
  id: number;
  tenant_id: number | null;
  provider: string;
  service: string | null;
  category: string;
  description: string | null;
  billing_period_start: string;
  billing_period_end: string;
  amount_usd: number;
  currency: string;
  invoice_number: string | null;
  invoice_file_name: string | null;
  has_invoice: boolean;
  source: string;
  external_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface InfraSummary {
  period: { startDate: string; endDate: string; compareStartDate: string; compareEndDate: string };
  total: number;
  prior_total: number;
  change_pct: number | null;
  entry_count: number;
  by_category: Array<{ category: string; amount: number; prior_amount: number; change_pct: number | null }>;
  by_provider: Array<{ provider: string; amount: number; prior_amount: number }>;
  monthly_trend: Array<{ month: string; provider: string; amount: number }>;
  detail: InfraEntry[];
}

interface TotalSpend {
  variable_cost_usd: number;
  variable_cost_usd_prior: number;
  infrastructure_cost_usd: number;
  infrastructure_cost_usd_prior: number;
  total_cost_usd: number;
  total_cost_usd_prior: number;
  change_pct: number | null;
  variable_share_pct: number;
  infrastructure_share_pct: number;
}

interface Props {
  startDate: string;
  endDate: string;
  onTotalSpendChange?: (totalSpend: TotalSpend | null) => void;
}

const PROVIDERS = [
  'railway', 'aws', 'azure', 'gcp', 'github', 'vercel', 'netlify',
  'cloudflare', 'datadog', 'sentry', 'elastic', 'mongodb_atlas',
  'redis_cloud', 'postgres_cloud', 'anthropic', 'openai', 'google',
  'perplexity', 'mistral', 'other',
];
const CATEGORIES = [
  'hosting', 'compute', 'database', 'storage', 'cdn',
  'monitoring', 'ci_cd', 'security', 'llm_provider', 'observability',
  'email', 'analytics', 'other',
];

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  hosting: Cloud,
  compute: Server,
  database: Database,
  storage: HardDrive,
  cdn: Zap,
  monitoring: ActivityIcon,
  ci_cd: GitBranch,
  security: ShieldCheck,
  llm_provider: Zap,
  observability: ActivityIcon,
  email: FileText,
  analytics: ActivityIcon,
  other: Server,
};

const CHART_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#ef4444', '#6366f1', '#84cc16', '#f97316', '#14b8a6', '#a855f7'];

function fmtUsd(n: number) {
  return (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtPct(n: number | null) {
  if (n == null || !isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function prettyLabel(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const InfrastructureCostsWidget: React.FC<Props> = ({ startDate, endDate, onTotalSpendChange }) => {
  const [summary, setSummary] = useState<InfraSummary | null>(null);
  const [totalSpend, setTotalSpend] = useState<TotalSpend | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<InfraEntry | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [downloadingBundle, setDownloadingBundle] = useState(false);

  const periodQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.set('startDate', startDate);
    p.set('endDate', endDate);
    return p.toString();
  }, [startDate, endDate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, tRes] = await Promise.all([
        api.get(`/api/admin/infrastructure-costs/summary?${periodQuery}`),
        api.get(`/api/admin/infrastructure-costs/total-spend?${periodQuery}`),
      ]);
      if (sRes.data?.success) setSummary(sRes.data);
      if (tRes.data?.success) {
        setTotalSpend(tRes.data);
        onTotalSpendChange?.(tRes.data);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to load infrastructure costs');
    } finally {
      setLoading(false);
    }
  }, [periodQuery, onTotalSpendChange]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this infrastructure cost entry? This cannot be undone.')) return;
    try {
      await api.delete(`/api/admin/infrastructure-costs/${id}`);
      toast.success('Entry deleted');
      fetchAll();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Delete failed');
    }
  };

  const handleDownloadBundle = async () => {
    try {
      setDownloadingBundle(true);
      const res = await api.get(
        `/api/admin/infrastructure-costs/expense-bundle.zip?${periodQuery}`,
        { responseType: 'blob', timeout: 120_000 },
      );
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expense-report-${startDate}_to_${endDate}.zip`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Expense report downloaded');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Bundle download failed');
    } finally {
      setDownloadingBundle(false);
    }
  };

  const handleDownloadInvoice = async (entry: InfraEntry) => {
    try {
      const res = await api.get(`/api/admin/infrastructure-costs/${entry.id}/invoice`, {
        responseType: 'blob',
        timeout: 60_000,
      });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = entry.invoice_file_name || `invoice-${entry.id}.pdf`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err: any) {
      toast.error('Could not download invoice');
    }
  };

  const trendChartData = useMemo(() => {
    if (!summary) return { data: [], providers: [] as string[] };
    const byMonth: Record<string, any> = {};
    const providers = new Set<string>();
    for (const r of summary.monthly_trend) {
      providers.add(r.provider);
      if (!byMonth[r.month]) byMonth[r.month] = { month: r.month };
      byMonth[r.month][r.provider] = (byMonth[r.month][r.provider] || 0) + r.amount;
    }
    return {
      data: Object.values(byMonth).sort((a: any, b: any) => a.month.localeCompare(b.month)),
      providers: Array.from(providers).sort(),
    };
  }, [summary]);

  const visibleDetail = useMemo(() => {
    if (!summary) return [];
    return showAll ? summary.detail : summary.detail.slice(0, 8);
  }, [summary, showAll]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2 bg-violet-100 rounded-lg">
            <Server className="w-5 h-5 text-violet-700" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-gray-900">Infrastructure Costs</h3>
            <p className="text-sm text-gray-600">
              Hosting, compute, database, monitoring and other fixed-cost services. Combined with LLM token spend below for the complete picture.
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleDownloadBundle}
            disabled={downloadingBundle}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50"
            title="Download a ZIP with consolidated PDF report, LLM/infra detail spreadsheets, and all uploaded invoice PDFs for the selected period"
          >
            {downloadingBundle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download Invoices
          </button>
          <button
            onClick={() => { setEditing(null); setShowCreate(true); }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <Plus className="w-4 h-4" />
            Add Entry
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Total Spend strip — variable + fixed */}
        {totalSpend && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">Variable (LLM tokens)</div>
              <div className="text-2xl font-bold text-emerald-900 mt-1">{fmtUsd(totalSpend.variable_cost_usd)}</div>
              <div className="text-xs text-emerald-700 mt-1">{totalSpend.variable_share_pct.toFixed(1)}% of total</div>
            </div>
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-4">
              <div className="text-xs font-semibold text-violet-800 uppercase tracking-wide">Infrastructure (fixed)</div>
              <div className="text-2xl font-bold text-violet-900 mt-1">{fmtUsd(totalSpend.infrastructure_cost_usd)}</div>
              <div className="text-xs text-violet-700 mt-1">{totalSpend.infrastructure_share_pct.toFixed(1)}% of total</div>
            </div>
            <div className="rounded-lg border-2 border-gray-300 bg-gradient-to-r from-gray-50 to-white p-4">
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Total Spend</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{fmtUsd(totalSpend.total_cost_usd)}</div>
              <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                {totalSpend.change_pct != null && (
                  <>
                    {totalSpend.change_pct > 0
                      ? <TrendingUp className="w-3 h-3 text-red-600" />
                      : <TrendingDown className="w-3 h-3 text-emerald-600" />}
                    <span className={totalSpend.change_pct > 0 ? 'text-red-700' : 'text-emerald-700'}>
                      {fmtPct(totalSpend.change_pct)} vs prior period
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Category cards */}
        {summary && summary.by_category.length > 0 && (
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-2">By Category — current period</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {summary.by_category.slice(0, 8).map((c) => {
                const Icon = CATEGORY_ICONS[c.category] || Server;
                return (
                  <div key={c.category} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-gray-600" />
                      <span className="text-xs font-medium text-gray-700">{prettyLabel(c.category)}</span>
                    </div>
                    <div className="text-lg font-bold text-gray-900 mt-1">{fmtUsd(c.amount)}</div>
                    {c.change_pct != null && (
                      <div className={`text-xs mt-0.5 ${c.change_pct > 0 ? 'text-red-700' : c.change_pct < 0 ? 'text-emerald-700' : 'text-gray-500'}`}>
                        {fmtPct(c.change_pct)} vs prior
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 12-month trend chart */}
        {summary && trendChartData.data.length > 0 && (
          <div>
            <div className="text-sm font-semibold text-gray-700 mb-2">12-Month Trend by Provider</div>
            <div className="h-72 w-full">
              <ResponsiveContainer>
                <BarChart data={trendChartData.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip formatter={(v: any) => fmtUsd(Number(v))} />
                  <Legend />
                  {trendChartData.providers.map((p, i) => (
                    <Bar key={p} dataKey={p} stackId="infra" fill={CHART_COLORS[i % CHART_COLORS.length]} name={prettyLabel(p)} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Provider breakdown table */}
        {summary && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-gray-700">Line items — current period ({summary.entry_count})</div>
              {summary.detail.length > 8 && (
                <button
                  onClick={() => setShowAll((v) => !v)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {showAll ? 'Show less' : `Show all ${summary.detail.length}`}
                </button>
              )}
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Provider</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Service</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Period</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Amount</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Invoice</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleDetail.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                        No infrastructure costs recorded for this period yet. Click <span className="font-semibold">Add Entry</span> to record one.
                      </td>
                    </tr>
                  )}
                  {visibleDetail.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">{prettyLabel(row.provider)}</td>
                      <td className="px-3 py-2 text-gray-700">{row.service || '—'}</td>
                      <td className="px-3 py-2 text-gray-700">{prettyLabel(row.category)}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{row.billing_period_start} → {row.billing_period_end}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">{fmtUsd(row.amount_usd)}</td>
                      <td className="px-3 py-2">
                        {row.has_invoice ? (
                          <button
                            onClick={() => handleDownloadInvoice(row)}
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            title={row.invoice_file_name || 'Download invoice'}
                          >
                            <Paperclip className="w-3 h-3" />
                            {row.invoice_number || 'PDF'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => { setEditing(row); setShowCreate(true); }}
                            className="p-1 text-gray-500 hover:text-blue-600"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(row.id)}
                            className="p-1 text-gray-500 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {loading && !summary && (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading…
          </div>
        )}
      </div>

      {showCreate && (
        <InfraEntryModal
          existing={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

// ─── Entry modal ──────────────────────────────────────────────────────────────

interface ModalProps {
  existing: InfraEntry | null;
  onClose: () => void;
  onSaved: () => void;
}

const InfraEntryModal: React.FC<ModalProps> = ({ existing, onClose, onSaved }) => {
  const today = new Date();
  const firstOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)).toISOString().slice(0, 10);
  const endOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);

  const [form, setForm] = useState({
    provider: existing?.provider || 'railway',
    service: existing?.service || '',
    category: existing?.category || 'hosting',
    description: existing?.description || '',
    billing_period_start: existing?.billing_period_start || firstOfMonth,
    billing_period_end: existing?.billing_period_end || endOfMonth,
    amount_usd: existing?.amount_usd ? String(existing.amount_usd) : '',
    invoice_number: existing?.invoice_number || '',
    notes: existing?.notes || '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.amount_usd || Number(form.amount_usd) < 0) {
      toast.error('Amount must be a positive number');
      return;
    }
    if (form.billing_period_end < form.billing_period_start) {
      toast.error('End date must be on or after start date');
      return;
    }
    setSubmitting(true);
    try {
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => body.append(k, String(v ?? '')));
      if (file) body.append('invoice', file);
      if (existing) {
        await api.patch(`/api/admin/infrastructure-costs/${existing.id}`, body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Entry updated');
      } else {
        await api.post('/api/admin/infrastructure-costs', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Entry created');
      }
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-violet-600" />
            <h3 className="text-lg font-bold text-gray-900">
              {existing ? 'Edit infrastructure cost' : 'Add infrastructure cost'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Provider *</label>
              <select
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                required
              >
                {PROVIDERS.map((p) => (
                  <option key={p} value={p}>{prettyLabel(p)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Category *</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                required
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{prettyLabel(c)}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Service / Plan</label>
            <input
              type="text"
              value={form.service}
              onChange={(e) => setForm({ ...form, service: e.target.value })}
              placeholder="e.g. Postgres Pro, EC2 t3.medium, Pro plan"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Period from *</label>
              <input
                type="date"
                value={form.billing_period_start}
                onChange={(e) => setForm({ ...form, billing_period_start: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Period to *</label>
              <input
                type="date"
                value={form.billing_period_end}
                onChange={(e) => setForm({ ...form, billing_period_end: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Amount (USD) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.amount_usd}
                onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
                placeholder="0.00"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Invoice number</label>
            <input
              type="text"
              value={form.invoice_number}
              onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
              placeholder="INV-2026-04-001"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="Production database hosting (us-east-2)"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Invoice PDF (optional)
            </label>
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm"
            />
            {existing?.has_invoice && !file && (
              <div className="text-xs text-gray-500 mt-1">
                <Paperclip className="inline w-3 h-3 mr-1" />
                Existing file: {existing.invoice_file_name || 'attached'}. Choose a new file to replace.
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Internal notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {existing ? 'Save changes' : 'Create entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InfrastructureCostsWidget;
